import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { Icon } from '@/components/core/Icon';
import { Loader } from '@/components/core/Loader';
import { useTranslation } from '@/hooks/useTranslation';

export interface FoundItemsLabelProps {
    visibleRowCount: number;
    numberFound: number;
    maxNumberFound: number;
    countingItems?: boolean;
    hasCursor?: boolean;
    onRefreshCount?: () => void;
}

/**
 * "N of M items found" counter.
 * Parity with `search-filter.tpl.html` lines 160-197 and the partial
 * implementation previously embedded in `itemsList.tsx`.
 */
export function FoundItemsLabel({
    visibleRowCount,
    numberFound,
    maxNumberFound,
    countingItems = false,
    hasCursor = false,
    onRefreshCount,
}: FoundItemsLabelProps) {
    const { t } = useTranslation();

    if (visibleRowCount <= 0) {
        return null;
    }

    const refreshButton = onRefreshCount ? (
        <button
            type="button"
            onClick={onRefreshCount}
            className="tw-ml-0.5 tw-align-text-top tw-inline-flex"
            aria-label={t('ITEM.NUMBER_FOUND_COUNTING')}
        >
            <Icon icon={ArrowPathIcon} size="sm" variant="secondary" />
        </button>
    ) : null;

    if (countingItems) {
        return (
            <div id="found-items-label" className="tw-text-right">
                <span>
                    {t('ITEM.NUMBER_FOUND_COUNTING', {
                        from: 1,
                        to: visibleRowCount,
                    })}
                </span>
                <Loader size="sm" className="tw-inline-block tw-align-text-top tw-ml-1" />
            </div>
        );
    }

    if (
        numberFound >= visibleRowCount
        && numberFound <= maxNumberFound
    ) {
        return (
            <div id="found-items-label" className="tw-text-right" data-items-found={numberFound}>
                <span>
                    {t('ITEM.NUMBER_FOUND', {
                        from: 1,
                        to: visibleRowCount,
                        count: numberFound,
                    })}
                </span>
            </div>
        );
    }

    if (numberFound > maxNumberFound) {
        return (
            <div id="found-items-label" className="tw-text-right" data-items-found={numberFound}>
                <span>
                    {t('ITEM.NUMBER_FOUND_APPROXIMATELY', {
                        from: 1,
                        to: visibleRowCount,
                        count: maxNumberFound,
                    })}
                </span>
                {refreshButton}
            </div>
        );
    }

    if (numberFound < visibleRowCount && hasCursor) {
        return (
            <div id="found-items-label" className="tw-text-right">
                <span>
                    {t('ITEM.NUMBER_FOUND_APPROXIMATELY', {
                        from: 1,
                        to: visibleRowCount,
                        count: visibleRowCount,
                    })}
                </span>
                {refreshButton}
            </div>
        );
    }

    if (numberFound < visibleRowCount && !hasCursor) {
        return (
            <div id="found-items-label" className="tw-text-right">
                <span>
                    {t('ITEM.NUMBER_FOUND', {
                        from: 1,
                        to: visibleRowCount,
                        count: visibleRowCount,
                    })}
                </span>
            </div>
        );
    }

    return null;
}
